import { useEffect, useState } from "react";
import { Eye, EyeOff, Trash2, Star, MessageSquare, X } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const FILTERS = [
  { id: "all", label: "Tous" },
  { id: "visible", label: "Visibles" },
  { id: "hidden", label: "Masqués" },
];

export default function AdminReviewsTab() {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyOpen, setReplyOpen] = useState(null); // review id currently being replied to
  const [replyDraft, setReplyDraft] = useState("");
  const [replySaving, setReplySaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/reviews?status=${filter}&limit=300`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Impossible de charger les avis");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const toggleVisibility = async (review) => {
    const newHidden = !review.hidden;
    try {
      await api.patch(
        `/admin/reviews/${review.id}/visibility?hidden=${newHidden}`
      );
      toast.success(newHidden ? "Avis masqué" : "Avis re-publié");
      load();
    } catch {
      toast.error("Échec de la modification");
    }
  };

  const remove = async (review) => {
    if (
      !confirm(
        `Supprimer définitivement l'avis de ${review.user_name} sur "${review.product_name}" ?`
      )
    )
      return;
    try {
      await api.delete(`/admin/reviews/${review.id}`);
      toast.success("Avis supprimé");
      load();
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  const openReply = (review) => {
    setReplyOpen(review.id);
    setReplyDraft(review.admin_reply || "");
  };
  const cancelReply = () => {
    setReplyOpen(null);
    setReplyDraft("");
  };
  const saveReply = async (review) => {
    setReplySaving(true);
    try {
      await api.patch(`/admin/reviews/${review.id}/reply`, {
        reply: replyDraft,
      });
      toast.success(replyDraft.trim() ? "Réponse publiée" : "Réponse supprimée");
      setReplyOpen(null);
      setReplyDraft("");
      load();
    } catch {
      toast.error("Échec de la publication");
    } finally {
      setReplySaving(false);
    }
  };

  return (
    <div data-testid="admin-reviews-tab">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-[0.25em] border transition ${
              filter === f.id
                ? "bg-white text-[#001F3F] border-white"
                : "bg-transparent text-white/70 border-white/20 hover:border-white"
            }`}
            data-testid={`reviews-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-white/60">
          {items.length} avis
        </span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-sm text-white/60">
          Chargement...
        </div>
      ) : items.length === 0 ? (
        <div className="border border-white/10 bg-[#0A2A4F] p-8 text-center text-sm text-white/60">
          Aucun avis dans cette catégorie.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <article
              key={r.id}
              className={`border p-5 transition ${
                r.hidden
                  ? "border-white/5 bg-[#001F3F] opacity-60"
                  : "border-white/10 bg-[#0A2A4F]"
              }`}
              data-testid={`admin-review-${r.id}`}
            >
              <div className="flex flex-wrap justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-[#C5CDD8] uppercase tracking-widest font-bold truncate">
                    {r.product_name}
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-sm text-white">
                    <span className="font-bold">{r.user_name}</span>
                    {r.verified && (
                      <span className="text-[10px] uppercase tracking-widest text-[#A6E0B5]">
                        Achat vérifié
                      </span>
                    )}
                    {r.hidden && (
                      <span className="text-[10px] uppercase tracking-widest text-[#FF6B6B]">
                        Masqué
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      size={14}
                      strokeWidth={1.5}
                      className={
                        n <= r.rating
                          ? "fill-[#FFD700] text-[#FFD700]"
                          : "text-white/20"
                      }
                    />
                  ))}
                </div>
              </div>
              <div className="text-sm font-bold text-white mt-2">{r.title}</div>
              <p className="text-sm text-white/70 mt-1 whitespace-pre-line">
                {r.body}
              </p>
              {Array.isArray(r.photos) && r.photos.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {r.photos.map((u, i) => (
                    <img
                      key={`${r.id}-${i}`}
                      src={u}
                      alt=""
                      className="w-16 h-16 object-cover border border-white/20"
                    />
                  ))}
                </div>
              )}
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs">
                <div className="text-white/40 uppercase tracking-widest">
                  {new Date(r.created_at).toLocaleDateString("fr-FR")}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openReply(r)}
                    className={`inline-flex items-center gap-1 px-3 py-2 border uppercase tracking-widest font-bold transition ${
                      r.admin_reply
                        ? "border-[#C5CDD8]/60 text-[#C5CDD8] hover:border-white hover:text-white"
                        : "border-white/20 text-white/80 hover:border-white hover:text-white"
                    }`}
                    data-testid={`review-reply-${r.id}`}
                  >
                    <MessageSquare size={12} />
                    {r.admin_reply ? "Modifier réponse" : "Répondre"}
                  </button>
                  <button
                    onClick={() => toggleVisibility(r)}
                    className="inline-flex items-center gap-1 px-3 py-2 border border-white/20 text-white/80 hover:border-white hover:text-white uppercase tracking-widest font-bold"
                    data-testid={`review-toggle-${r.id}`}
                  >
                    {r.hidden ? (
                      <>
                        <Eye size={12} /> Re-publier
                      </>
                    ) : (
                      <>
                        <EyeOff size={12} /> Masquer
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => remove(r)}
                    className="inline-flex items-center gap-1 px-3 py-2 border border-white/10 text-white/50 hover:border-[#FF6B6B] hover:text-[#FF6B6B] uppercase tracking-widest font-bold"
                    data-testid={`review-delete-${r.id}`}
                  >
                    <Trash2 size={12} /> Supprimer
                  </button>
                </div>
              </div>

              {r.admin_reply && replyOpen !== r.id && (
                <div
                  className="mt-3 pl-4 border-l-2 border-[#C5CDD8]/40 bg-white/[0.03] py-3 pr-3"
                  data-testid={`admin-reply-preview-${r.id}`}
                >
                  <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
                    Réponse publiée
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-white/80 whitespace-pre-line">
                    {r.admin_reply}
                  </p>
                </div>
              )}

              {replyOpen === r.id && (
                <div
                  className="mt-4 border border-white/15 bg-[#001F3F] p-4"
                  data-testid={`admin-reply-editor-${r.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
                      Réponse publique de Nord Stella Tech
                    </div>
                    <button
                      onClick={cancelReply}
                      className="text-white/50 hover:text-white"
                      aria-label="Annuler"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <textarea
                    rows={3}
                    value={replyDraft}
                    onChange={(e) => setReplyDraft(e.target.value)}
                    placeholder="Merci pour votre retour…"
                    maxLength={1500}
                    className="w-full border border-white/20 bg-[#0A2A4F] text-white text-sm px-3 py-2 focus:outline-none focus:border-white"
                    data-testid={`admin-reply-input-${r.id}`}
                  />
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div className="text-[10px] text-white/40">
                      Affichée publiquement sous l'avis sur la page produit.
                    </div>
                    <div className="flex items-center gap-2">
                      {r.admin_reply && (
                        <button
                          onClick={() => {
                            setReplyDraft("");
                            saveReply({ ...r, admin_reply: "" });
                          }}
                          disabled={replySaving}
                          className="px-3 py-2 border border-white/10 text-white/50 hover:border-[#FF6B6B] hover:text-[#FF6B6B] uppercase tracking-widest text-[10px] font-bold disabled:opacity-50"
                          data-testid={`admin-reply-remove-${r.id}`}
                        >
                          Retirer
                        </button>
                      )}
                      <button
                        onClick={() => saveReply(r)}
                        disabled={replySaving}
                        className="px-4 py-2 bg-white text-[#001F3F] hover:bg-[#C5CDD8] uppercase tracking-widest text-[10px] font-bold disabled:opacity-50"
                        data-testid={`admin-reply-save-${r.id}`}
                      >
                        {replySaving ? "Envoi…" : "Publier la réponse"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
